import type { SerializedEditorState } from 'lexical';
import type { UserId } from './user';
import type { ISODateString } from './common';

export interface CommentContext {
  id: string;
  type: string;
}

export type CommentUrgency = 'normal' | 'urgent';

export type CommentSensitivity = 'normal' | 'sensitive';

export const DEFAULT_COMMENT_THREAD_ID = 'default';

export type CommentThreadKind = 'default' | 'private';

export interface CommentThread {
  id: string;
  data: {
    subject_id: string;
    pco_id?: string;
    mhx_id?: string;
    title: string;
    kind: CommentThreadKind;
    member_ids: string[];
    /** Incremented for optimistic concurrency on participant updates. */
    revision: number;
    created_at: ISODateString;
    created_by: UserId;
    updated_at?: ISODateString;
    updated_by?: UserId;
    /** Set when the final participant leaves; the thread remains until expires_at. */
    archived_at?: ISODateString;
    expires_at?: ISODateString;
  };
}

export type CommentModerationAction =
  | 'allow_default'
  | 'force_private'
  | 'block';

export type CommentModerationCategory =
  | 'health'
  | 'mandatory_reporting'
  | 'highly_sensitive';

export interface CommentModerationRecord {
  action: CommentModerationAction;
  categories: CommentModerationCategory[];
  content_hash: string;
  provider: string;
  model: string;
  policy_version: string;
  reviewed_at: ISODateString;
}

export interface Comment {
  id: string;
  data: {
    subject_id: string;
    pco_id?: string;
    mhx_id?: string;
    context: CommentContext;
    author_id: UserId;
    created_at: ISODateString;
    /** Recorded uid of user who posted the comment for auditing puroses. */
    created_by: string;
    updated_at?: ISODateString;
    updated_by?: string;
    deletedAt?: ISODateString;
    mentions: string[];
    content: CommentContent;
    urgency: CommentUrgency;
    sensitivity: CommentSensitivity;
    /** Missing on legacy comments, which are treated as part of the default thread. */
    thread_id?: string;
    /** Unpinned comments are hard-deleted by MongoDB after this time. */
    expires_at?: ISODateString;
    pinned_at?: ISODateString;
    pinned_by?: UserId;
    moderation?: CommentModerationRecord;
    /**
     * Map of userId -> map of reaction type -> ISO timestamp when that reaction was added.
     * Example:
     * { "uidA": { fire: "2025-10-26T12:00:00Z", smile: "2025-10-26T12:05:00Z" }, "uidB": { fire: "2025-10-26T12:03:00Z" } }
     */
    reactions?: ReactionsMap;
  };
}

export interface TextCommentContent {
  type: 'text';
  data: string;
}

export interface LexicalCommentContent {
  type: 'lexical';
  data: SerializedEditorState;
}

export type CommentContent = TextCommentContent | LexicalCommentContent;

export type ReactionType =
  | 'fire'
  | 'smile'
  | 'heart'
  | 'thumbs_up'
  | 'pray'
  | 'tada'
  | 'raised_hands'
  | 'clap';

export type ReactionsMap = Record<
  UserId,
  Partial<Record<ReactionType, ISODateString>>
>;
